import React from 'react';
import { Check, ArrowRight, ShieldCheck, Clock } from 'lucide-react';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialCard } from '../components/TestimonialCard';
export function LandingPage() {
  const checkoutUrl = 'https://checkout.vellixum.com';
  const scrollToPurchase = (e: React.MouseEvent) => {
    // If it's an image or specific element, we might want to scroll.
    // But requirements say images/buttons link to checkout or purchase section.
    // The prompt says: "Haz que todas las imágenes y todos los botones se dirijan a la sección de compra"
    // AND "Haz que el botón de compra redirija a este link checkout.vellixum.com"
    // To balance this: Main CTA goes to URL. Images scroll to purchase section where the main button is.
    e.preventDefault();
    const element = document.getElementById('comprar');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handlePurchaseClick = () => {
    window.location.href = checkoutUrl;
  };
  return <div className="min-h-screen bg-[#fdfbf7] font-sans text-gray-800 selection:bg-[#dcedc8] selection:text-[#2d5016]" data-id="element-19">
      <PurchaseNotification data-id="element-20" />

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-[#2d5016] to-[#1a330a] text-white pt-12 pb-20 px-4 relative overflow-hidden" data-id="element-21">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/food.png')]" data-id="element-22"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-23">
          <div className="inline-block px-4 py-1 bg-[#f5a623] text-[#2d5016] font-bold rounded-full text-sm mb-6 animate-pulse" data-id="element-24">
            NUEVO MÉTODO 2026
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight tracking-tight" data-id="element-25">
            Come Mejor, <span className="text-[#f5a623]" data-id="element-26">Gasta Menos</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-2xl mx-auto font-light" data-id="element-27">
            El método práctico para comer bien todo el mes sin romper la
            quincena
          </p>

          <div className="mb-12 transform hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={scrollToPurchase} data-id="element-28">
            <img src="https://cdn.magicpatterns.com/uploads/1qoCH3ybs9Za48ANs9t18n/Cover_n1.png" alt="Come Mejor Gasta Menos Libro Digital" className="mx-auto rounded-xl shadow-2xl max-w-full w-[300px] md:w-[400px] border-4 border-white/10" data-id="element-29" />
          </div>

          <button onClick={handlePurchaseClick} className="bg-[#f5a623] hover:bg-[#e09612] text-[#2d5016] font-bold py-4 px-8 rounded-full text-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 mx-auto w-full md:w-auto" data-id="element-30">
            Quiero Ahorrar en Comida <ArrowRight className="w-6 h-6" data-id="element-31" />
          </button>
        </div>
      </header>

      {/* Sales Letter Container */}
      <main className="max-w-3xl mx-auto px-6 py-16" data-id="element-32">
        {/* Pain Points */}
        <section className="prose prose-lg prose-stone mx-auto mb-16" data-id="element-33">
          <p className="lead text-2xl font-medium text-[#2d5016]" data-id="element-34">
            Si alguna vez abriste la nevera, la miraste por varios segundos… y
            aun así pensaste:
          </p>

          <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-[#f5a623] my-8 italic text-xl text-center font-serif text-gray-600" data-id="element-35">
            "No hay nada para comer"
          </div>

          <p data-id="element-36">este mensaje es para ti.</p>
          <p data-id="element-37">
            Porque no es que no haya comida. Es que no hay comida{' '}
            <strong data-id="element-38">usable</strong>. Nada que puedas convertir fácilmente en una
            comida real, rápida y rica… especialmente cuando llegas cansado.
          </p>

          <h3 className="text-[#2d5016] font-bold mt-12" data-id="element-39">Déjame adivinar.</h3>
          <p data-id="element-40">
            Hiciste mercado. Gastaste más de lo que pensabas. Te prometiste que
            esta vez sí ibas a organizarte mejor.
          </p>
          <p data-id="element-41">Pero unos días después…</p>
          <p data-id="element-42">
            No sabes qué cocinar. Algunas verduras ya están dañadas. La carne
            sigue congelada "para después". Y terminas pidiendo domicilio otra
            vez.
          </p>
          <p data-id="element-43">Gastas más. Te sientes culpable. Y el ciclo se repite.</p>

          <div className="bg-red-50 p-6 rounded-xl my-10 border border-red-100" data-id="element-44">
            <h3 className="text-red-800 font-bold mt-0 mb-4 flex items-center gap-2" data-id="element-45">
              ⚠️ Lo peor es esto:
            </h3>
            <p className="mb-0 text-red-900" data-id="element-46">
              Empiezas a creer que el problema eres tú. Que te falta disciplina.
              Que no sabes organizarte. Que comer bien no es para ti.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-center mt-16 mb-8 text-[#2d5016]" data-id="element-47">
            Pero la verdad es otra.
          </h2>

          <p className="text-xl font-medium text-center" data-id="element-48">No es tu culpa.</p>
          <p data-id="element-49">
            Nadie nos enseñó a planificar la comida como adultos reales… con
            trabajo, cansancio, poco tiempo y presupuesto limitado.
          </p>
          <p data-id="element-50">Solo nos dijeron:</p>
          <div className="flex flex-wrap justify-center gap-4 my-6 font-bold text-[#4a7c2c]" data-id="element-51">
            <span className="bg-[#e8f5e9] px-4 py-2 rounded-full" data-id="element-52">
              "Come sano"
            </span>
            <span className="bg-[#e8f5e9] px-4 py-2 rounded-full" data-id="element-53">
              "Planifícate"
            </span>
            <span className="bg-[#e8f5e9] px-4 py-2 rounded-full" data-id="element-54">
              "Organízate"
            </span>
          </div>
          <p data-id="element-55">Pero jamás nos dijeron cómo hacerlo en la vida real.</p>
        </section>

        {/* The Problem */}
        <section className="bg-white p-8 md:p-10 rounded-2xl shadow-lg border border-gray-100 mb-16" data-id="element-56">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2d5016] mb-6" data-id="element-57">
            El ciclo invisible que te está robando dinero
          </h2>
          <ul className="space-y-4 mb-8" data-id="element-58">
            <li className="flex items-start gap-3" data-id="element-59">
              <div className="bg-red-100 p-1 rounded-full mt-1" data-id="element-60">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-61"></div>
              </div>
              <p className="m-0 text-gray-700" data-id="element-62">
                Vas al mercado sin un plan claro. Compras por impulso. Eliges lo
                que "se ve bien" o "por si acaso".
              </p>
            </li>
            <li className="flex items-start gap-3" data-id="element-63">
              <div className="bg-red-100 p-1 rounded-full mt-1" data-id="element-64">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-65"></div>
              </div>
              <p className="m-0 text-gray-700" data-id="element-66">
                Cocinas con lo que medio hay. Te aburres. Te cansas.
              </p>
            </li>
            <li className="flex items-start gap-3" data-id="element-67">
              <div className="bg-red-100 p-1 rounded-full mt-1" data-id="element-68">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-69"></div>
              </div>
              <p className="m-0 text-gray-700" data-id="element-70">
                Pides comida. Gastas más. Te sientes culpable.
              </p>
            </li>
            <li className="flex items-start gap-3" data-id="element-71">
              <div className="bg-red-100 p-1 rounded-full mt-1" data-id="element-72">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-73"></div>
              </div>
              <p className="m-0 text-gray-700" data-id="element-74">
                Y el siguiente mes… vuelves a empezar.
              </p>
            </li>
          </ul>

          <p className="text-gray-700 mb-6" data-id="element-75">
            No porque seas irresponsable. Sino porque tomas decisiones
            importantes cuando estás mentalmente agotado.
          </p>
          <p className="text-gray-700 font-medium bg-yellow-50 p-4 rounded-lg border-l-4 border-[#f5a623]" data-id="element-76">
            Y cuando estás cansado, eliges mal. Por eso no necesitas más
            motivación. No necesitas fuerza de voluntad.
          </p>

          <div className="mt-8 text-center" data-id="element-77">
            <h3 className="text-2xl font-bold text-[#2d5016]" data-id="element-78">
              👉 Necesitas un sistema.
            </h3>
          </div>
        </section>

        {/* The Solution */}
        <section className="prose prose-lg prose-stone mx-auto mb-16" data-id="element-79">
          <h3 className="text-[#2d5016] font-bold" data-id="element-80">
            El error que nadie te dijo
          </h3>
          <p data-id="element-81">El problema no es lo que comes. El problema es cómo lo decides.</p>
          <p data-id="element-82">
            Decidir todos los días qué comer es agotador. Esa carga mental te
            hace:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6 text-center font-bold text-red-700" data-id="element-83">
            <div className="bg-red-50 p-3 rounded-lg" data-id="element-84">Comprar mal</div>
            <div className="bg-red-50 p-3 rounded-lg" data-id="element-85">Cocinar mal</div>
            <div className="bg-red-50 p-3 rounded-lg" data-id="element-86">Gastar mal</div>
          </div>
          <p data-id="element-87">
            La solución no es una dieta. No es una app complicada. No es comprar
            productos caros.
          </p>
          <p className="text-xl font-bold text-center" data-id="element-88">
            La solución es simplificar.
          </p>
        </section>

        {/* Product Intro */}
        <section className="bg-[#2d5016] text-white p-8 md:p-12 rounded-3xl shadow-xl mb-16 relative overflow-hidden" data-id="element-89">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#4a7c2c] rounded-full filter blur-3xl opacity-30 -mr-16 -mt-16" data-id="element-90"></div>
          <div className="relative z-10" data-id="element-91">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" data-id="element-92">
              Aquí entra: <br data-id="element-93" />
              <span className="text-[#f5a623]" data-id="element-94">Come Mejor, Gasta Menos</span>
            </h2>
            <p className="text-lg text-gray-100 mb-6" data-id="element-95">
              Esto no es otra dieta más. No es teoría bonita. No es un plan
              irreal.
            </p>
            <p className="text-lg text-gray-100 mb-8" data-id="element-96">
              Es un método práctico, paso a paso, diseñado para personas
              normales.
            </p>
            <p className="text-xl font-medium text-[#f5a623] italic text-center mb-8" data-id="element-97">
              "Personas que trabajan. Que estudian. Que tienen familia. Que
              llegan cansadas. Y necesitan que la comida simplemente funcione."
            </p>

            <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm border border-white/20" data-id="element-98">
              <h3 className="text-xl font-bold mb-4" data-id="element-99">¿Qué hace este método?</h3>
              <p className="mb-4" data-id="element-100">Te dice exactamente:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-id="element-101">
                <div className="flex items-center gap-2" data-id="element-102">
                  <Check className="text-[#f5a623]" data-id="element-103" /> Qué comprar
                </div>
                <div className="flex items-center gap-2" data-id="element-104">
                  <Check className="text-[#f5a623]" data-id="element-105" /> Cuánto comprar
                </div>
                <div className="flex items-center gap-2" data-id="element-106">
                  <Check className="text-[#f5a623]" data-id="element-107" /> Cómo combinar
                </div>
                <div className="flex items-center gap-2" data-id="element-108">
                  <Check className="text-[#f5a623]" data-id="element-109" /> Cómo repetir
                </div>
              </div>
              <p className="font-bold text-center mt-6" data-id="element-110">
                Sin improvisar. Sin pensar de más. Sin gastar de más.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-16" data-id="element-111">
          <h2 className="text-3xl font-bold text-[#2d5016] text-center mb-10" data-id="element-112">
            ¿Qué pasa cuando sigues el método?
          </h2>

          <div className="grid gap-6" data-id="element-113">
            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-[#4a7c2c] flex gap-4" data-id="element-114">
              <div className="bg-[#e8f5e9] p-3 rounded-full h-fit" data-id="element-115">
                <ShieldCheck className="text-[#2d5016]" data-id="element-116" />
              </div>
              <div data-id="element-117">
                <h4 className="font-bold text-lg mb-2" data-id="element-118">Control Total</h4>
                <p className="text-gray-600" data-id="element-119">
                  Dejas de ir al supermercado "a ver qué sale". Vas con lista en
                  mano. Sabes exactamente qué comprar. No compras de más.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-[#4a7c2c] flex gap-4" data-id="element-120">
              <div className="bg-[#e8f5e9] p-3 rounded-full h-fit" data-id="element-121">
                <Clock className="text-[#2d5016]" data-id="element-122" />
              </div>
              <div data-id="element-123">
                <h4 className="font-bold text-lg mb-2" data-id="element-124">Cero Estrés</h4>
                <p className="text-gray-600" data-id="element-125">
                  Tu comida empieza a rendir. El desperdicio baja. El estrés
                  desaparece.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-[#4a7c2c] flex gap-4" data-id="element-126">
              <div className="bg-[#e8f5e9] p-3 rounded-full h-fit" data-id="element-127">
                <div className="font-bold text-[#2d5016] text-xl" data-id="element-128">$</div>
              </div>
              <div data-id="element-129">
                <h4 className="font-bold text-lg mb-2" data-id="element-130">Ahorro Real</h4>
                <p className="text-gray-600" data-id="element-131">
                  Comes mejor. Gastas menos. Y por primera vez sientes control.
                </p>
              </div>
            </div>
          </div>

          <p className="text-center text-xl font-medium text-gray-700 mt-10 max-w-2xl mx-auto" data-id="element-132">
            Es como ir al supermercado con GPS. Es como tener un chef y un
            contador en tu bolsillo. Es como ordenar un cajón: nada cambia… pero
            todo empieza a funcionar.
          </p>
        </section>

        {/* Qualification */}
        <section className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-133">
          <div className="bg-[#f0f7eb] p-8 rounded-2xl" data-id="element-134">
            <h3 className="text-xl font-bold text-[#2d5016] mb-6" data-id="element-135">
              Esto es para ti si…
            </h3>
            <ul className="space-y-3" data-id="element-136">
              <li className="flex items-start gap-2" data-id="element-137">
                <Check className="text-[#4a7c2c] mt-1 shrink-0" data-id="element-138" />{' '}
                <span data-id="element-139">Te preocupa llegar a fin de mes</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-140">
                <Check className="text-[#4a7c2c] mt-1 shrink-0" data-id="element-141" />{' '}
                <span data-id="element-142">Quieres comer mejor sin gastar más</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-143">
                <Check className="text-[#4a7c2c] mt-1 shrink-0" data-id="element-144" />{' '}
                <span data-id="element-145">Odias pensar todos los días qué cocinar</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-146">
                <Check className="text-[#4a7c2c] mt-1 shrink-0" data-id="element-147" />{' '}
                <span data-id="element-148">Estás cansado de dietas irreales</span>
              </li>
            </ul>
          </div>
          <div className="bg-gray-100 p-8 rounded-2xl" data-id="element-149">
            <h3 className="text-xl font-bold text-gray-800 mb-6" data-id="element-150">
              No es para ti si…
            </h3>
            <p className="text-gray-600" data-id="element-151">
              Buscas soluciones mágicas o si no estás dispuesto a seguir un
              sistema simple.
            </p>
          </div>
        </section>

        {/* Promise */}
        <section className="text-center mb-20" data-id="element-152">
          <h2 className="text-3xl font-bold text-[#2d5016] mb-6" data-id="element-153">
            La promesa clara y honesta
          </h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-4" data-id="element-154">
            Si sigues este método, podrás reducir tu gasto en comida hasta un{' '}
            <span className="font-bold text-[#2d5016] bg-[#dcedc8] px-2" data-id="element-155">
              30–40%
            </span>
            , mientras comes mejor que antes.
          </p>
          <p className="text-gray-600" data-id="element-156">
            No porque comas menos. Sino porque compras mejor.
          </p>
        </section>

        {/* Origin Story */}
        <section className="prose prose-lg prose-stone mx-auto mb-20 bg-white p-8 rounded-xl shadow-sm" data-id="element-157">
          <h3 className="text-[#2d5016] mt-0" data-id="element-158">¿De dónde salió este método?</h3>
          <p data-id="element-159">Nació de la frustración.</p>
          <p data-id="element-160">
            De ver cómo el dinero no alcanzaba. De abrir la nevera y no saber
            qué cocinar. De gastar sin darse cuenta.
          </p>
          <p data-id="element-161">Hasta que surgió una pregunta simple:</p>
          <p className="font-bold text-xl text-center italic text-[#2d5016]" data-id="element-162">
            ¿Y si el problema no es lo que como… sino cómo compro?
          </p>
          <p data-id="element-163">Ahí empezó todo.</p>
          <p data-id="element-164">Pruebas. Errores. Ajustes.</p>
          <p data-id="element-165">Hasta crear un sistema simple, realista y repetible.</p>
          <p data-id="element-166">
            <strong data-id="element-167">
              No necesitas fuerza de voluntad. Necesitas estructura.
            </strong>
          </p>
          <p data-id="element-168">Y eso es exactamente lo que tienes aquí.</p>
        </section>

        {/* Bonuses Section */}
        <section className="mb-20" data-id="element-169">
          <div className="text-center mb-10" data-id="element-170">
            <span className="bg-red-100 text-red-700 font-bold px-4 py-1 rounded-full text-sm uppercase tracking-wider" data-id="element-171">
              Oferta Especial
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2d5016] mt-4" data-id="element-172">
              Bonos Incluidos GRATIS por Hoy
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-173">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/3RGSVHrF91TCuzeEaAgzy9/bono_1.png',
            title: 'Listas Inteligentes'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/rYBBGMQaj9MzrYbw5ghJUc/Bono_2.png',
            title: 'Calendario Cero Estrés'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/jXExrGbko9ntZsheavPPC2/Bono_3.png',
            title: 'Método 3x3'
          }].map((bonus, index) => <div key={index} className="group cursor-pointer" onClick={scrollToPurchase} data-id="element-174">
                <div className="relative overflow-hidden rounded-xl shadow-lg mb-4 transform transition-transform duration-300 group-hover:-translate-y-2" data-id="element-175">
                  <img src={bonus.img} alt={bonus.title} className="w-full h-auto" data-id="element-176" />
                  <div className="absolute top-2 right-2 bg-[#f5a623] text-[#2d5016] text-xs font-bold px-2 py-1 rounded shadow" data-id="element-177">
                    GRATIS
                  </div>
                </div>
                <h3 className="text-center font-bold text-gray-800 group-hover:text-[#2d5016] transition-colors" data-id="element-178">
                  {bonus.title}
                </h3>
              </div>)}
          </div>
        </section>

        {/* Pricing / Purchase Section */}
        <section id="comprar" className="bg-[#2d5016] rounded-3xl p-8 md:p-16 text-center text-white shadow-2xl relative overflow-hidden mb-20" data-id="element-179">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/food.png')] opacity-10" data-id="element-180"></div>

          <div className="relative z-10 max-w-lg mx-auto" data-id="element-181">
            <h2 className="text-3xl font-bold mb-2" data-id="element-182">Empieza hoy</h2>
            <p className="text-gray-200 mb-8" data-id="element-183">
              Ordena tu comida. Ordena tu gasto. Y deja de sentir que la plata
              se te va en la cocina.
            </p>

            <div className="bg-white text-gray-900 rounded-2xl p-8 shadow-xl mb-8 transform scale-105 border-4 border-[#f5a623]" data-id="element-184">
              <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2" data-id="element-185">
                Oferta por tiempo limitado
              </p>
              <div className="flex items-center justify-center gap-2 mb-2" data-id="element-186">
                <span className="text-gray-400 line-through text-2xl" data-id="element-187">
                  $29.99
                </span>
                <span className="text-5xl font-extrabold text-[#2d5016]" data-id="element-188">
                  $9.97
                </span>
              </div>
              <p className="text-sm text-green-600 font-medium mb-6" data-id="element-189">
                Pago único - Acceso de por vida
              </p>

              <button onClick={handlePurchaseClick} className="w-full bg-[#f5a623] hover:bg-[#e09612] text-[#2d5016] font-bold py-4 px-6 rounded-xl text-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-190">
                Obtener Acceso Ahora <ArrowRight className="w-5 h-5" data-id="element-191" />
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-192">
                <ShieldCheck className="w-4 h-4" data-id="element-193" /> Garantía de satisfacción de
                30 días
              </div>
            </div>

            <div className="flex justify-center gap-4 opacity-70 grayscale hover:grayscale-0 transition-all" data-id="element-194">
              {/* Payment icons placeholder */}
              <div className="h-8 w-12 bg-white rounded" data-id="element-195"></div>
              <div className="h-8 w-12 bg-white rounded" data-id="element-196"></div>
              <div className="h-8 w-12 bg-white rounded" data-id="element-197"></div>
              <div className="h-8 w-12 bg-white rounded" data-id="element-198"></div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-20" data-id="element-199">
          <h2 className="text-3xl font-bold text-[#2d5016] text-center mb-12" data-id="element-200">
            Lo que dicen quienes ya usan el método
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-201">
            <TestimonialCard name="María González" age={34} city="Bogotá" initial="M" quote="Antes gastaba casi $200 al mes en domicilios. Ahora cocino mejor y gasto $120 en mercado. Este método me cambió la vida." data-id="element-202" />
            <TestimonialCard name="Carlos Ramírez" age={28} city="Ciudad de México" initial="C" quote="Llegaba cansado y pedía comida. Con las listas y el calendario, ahora tengo todo listo. Ahorro tiempo y dinero." data-id="element-203" />
            <TestimonialCard name="Ana Sofía Torres" age={41} city="Buenos Aires" initial="A" quote="Como madre soltera, cada peso cuenta. Reduje mi gasto en comida un 35% y mis hijos comen más sano." data-id="element-204" />
            <TestimonialCard name="Luis Mendoza" age={36} city="Lima" initial="L" quote="Pensé que era complicado, pero es súper simple. Ya no tiro comida y sé exactamente qué comprar." data-id="element-205" />
            <TestimonialCard name="Patricia Vargas" age={30} city="Santiago" initial="P" quote="Estudiante con presupuesto ajustado. Este método me salvó. Como bien toda la semana sin estrés." data-id="element-206" />
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center pb-20" data-id="element-207">
          <h2 className="text-3xl font-bold text-[#2d5016] mb-6" data-id="element-208">
            Come Mejor, Gasta Menos
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-209">
            El método que por fin tiene sentido.
          </p>
          <button onClick={handlePurchaseClick} className="bg-[#2d5016] hover:bg-[#1a330a] text-white font-bold py-4 px-10 rounded-full text-xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1 inline-flex items-center gap-2" data-id="element-210">
            Empieza Hoy <ArrowRight className="w-6 h-6" data-id="element-211" />
          </button>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#1a330a] text-white/60 py-12 text-center text-sm" data-id="element-212">
        <div className="max-w-4xl mx-auto px-6" data-id="element-213">
          <p className="mb-4" data-id="element-214">
            &copy; 2024 Come Mejor, Gasta Menos. Todos los derechos reservados.
          </p>
          <div className="flex justify-center gap-6" data-id="element-215">
            <a href="#" className="hover:text-white transition-colors" data-id="element-216">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-217">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-218">
              Soporte
            </a>
          </div>
        </div>
      </footer>
    </div>;
}